import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiDownload, apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '--';
  return Number(value || 0).toLocaleString('en-US');
}

function formatMoney(cents) {
  if (cents === null || cents === undefined || cents === '') return '--';
  const n = Number(cents || 0);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n / 100);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '--';
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return `${n.toFixed(1)}%`;
}

function formatBps(value) {
  if (value === null || value === undefined || value === '') return '--';
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return `${Math.round(n).toLocaleString('en-US')} bps`;
}

function formatDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatStateLabel(value) {
  if (!value) return '--';
  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatOwnershipTier(value) {
  const labels = {
    local: 'Local',
    regional: 'Regional',
    national: 'National',
    excluded: 'Excluded',
  };
  return labels[value] || formatStateLabel(value);
}

function formatFrequencyType(value) {
  const labels = {
    everyday: 'Everyday',
    periodic: 'Periodic',
    treat: 'Treat',
  };
  return labels[value] || formatStateLabel(value);
}

function formatMerchantLocation(row = {}) {
  const label = String(row.locationLabel || '').trim();
  if (label && label !== 'Location unknown') return label;
  const resolvedStoreCount = Number(row.storeAttribution?.resolvedStoreCount || 0);
  if (resolvedStoreCount > 0) return `${formatCount(resolvedStoreCount)} resolved ${resolvedStoreCount === 1 ? 'store' : 'stores'}`;
  const locationCount = Number(row.locationCount || 0);
  if (locationCount > 0) return `${formatCount(locationCount)} observed ${locationCount === 1 ? 'location' : 'locations'}`;
  return row.hasMerchantGeography ? 'Geo present' : 'No merchant location';
}

function formatMarketAttribution(row = {}, hasSpecificMarket = false) {
  if (!hasSpecificMarket) return row.marketLabel || 'Market not assigned';
  if (row.inTargetPolygon === true) return 'In selected market';
  if (row.inTargetPolygon === false) return 'Outside selected market';
  return 'Not attributed';
}

function formatLocationAssignmentStatus(value) {
  const labels = {
    geo_missing: 'Geo missing',
    resolution_pending: 'Resolution pending',
    market_assigned: 'Market assigned',
    market_not_assigned: 'Market not assigned',
    out_of_footprint: 'Out of footprint',
  };
  return labels[value] || formatStateLabel(value);
}

function getRepresentativeLocationAssignmentStatus(row = {}) {
  const locations = [
    ...(Array.isArray(row.observedLocations) ? row.observedLocations : []),
    ...(Array.isArray(row.storefronts) ? row.storefronts : []),
  ];
  const preferred = locations.find((loc) => row.inTargetPolygon === true && loc.inTargetMarket === true)
    || locations.find((loc) => row.marketKey && loc.marketKey === row.marketKey)
    || locations.find((loc) => loc.marketAssignmentStatus);
  return preferred?.marketAssignmentStatus || '';
}

function MerchantLocationCell({ row }) {
  const observedCount = Number(row?.locationCount || 0);
  const storefrontCount = Number(row?.storeAttribution?.resolvedStoreCount || 0);
  const secondary = storefrontCount > 0
    ? `${formatCount(storefrontCount)} itemized in details`
    : observedCount > 1
      ? 'Grouped merchant locations'
      : '';
  return (
    <>
      {formatMerchantLocation(row)}
      {secondary ? <span className="ops-loop-cell-note">{secondary}</span> : null}
    </>
  );
}

function MerchantMarketCell({ row, hasSpecificMarket }) {
  const assignmentStatus = getRepresentativeLocationAssignmentStatus(row);
  return (
    <>
      {formatMarketAttribution(row, hasSpecificMarket)}
      {assignmentStatus ? <span className="ops-loop-cell-note">{formatLocationAssignmentStatus(assignmentStatus)}</span> : null}
    </>
  );
}

function MerchantNameButton({ row, onOpen }) {
  return (
    <button type="button" className="ops-loop-table-link" onClick={() => onOpen(row)}>
      {row.displayName || row.normalizedMerchant}
    </button>
  );
}

function merchantObservedLocations(row = {}) {
  return Array.isArray(row.observedLocations) ? row.observedLocations : [];
}

function merchantStorefronts(row = {}) {
  return Array.isArray(row.storefronts) ? row.storefronts : [];
}

function frequencyIcon(value) {
  if (value === 'everyday') return 'E';
  if (value === 'treat') return 'T';
  if (value === 'periodic') return 'P';
  return '--';
}

function deltaLabel(delta) {
  return delta?.label || 'No previous-window comparison';
}

function statusIcon(statusKey) {
  if (statusKey === 'good') return 'OK';
  if (statusKey === 'watch') return '!';
  if (statusKey === 'act') return '!!';
  return '--';
}

function StatusPill({ status }) {
  if (!status || status.key === 'neutral') return null;
  const key = status.key || 'neutral';
  return (
    <span className={`ops-loop-status-pill ${key}`} aria-label={`Status: ${status.label || key}`}>
      <span className="ops-loop-status-icon" aria-hidden="true">{statusIcon(key)}</span>
      <span>{status.label || key}</span>
    </span>
  );
}

function HelperTip({ text }) {
  if (!text) return null;
  return (
    <span className="ops-loop-helper">
      <button type="button" className="ops-loop-helper-trigger" aria-label={`About ${text}`}>
        i
      </button>
      <span className="ops-loop-helper-popover" role="tooltip">{text}</span>
    </span>
  );
}

function MetricCard({ title, value, caption, delta, status = null, helper = '' }) {
  return (
    <article className={`ops-card ${status?.key && status.key !== 'neutral' ? `ops-loop-status-card ${status.key}` : ''}`}>
      <div className="ops-card-title ops-loop-card-title">
        <span className="ops-loop-title-left">
          <span>{title}</span>
          <HelperTip text={helper} />
        </span>
        <StatusPill status={status} />
      </div>
      <div className="ops-card-value">{value}</div>
      <div className="ops-card-caption">{caption}</div>
      {delta ? <div className="ops-card-caption">{deltaLabel(delta)}</div> : null}
    </article>
  );
}

function pct(value, total) {
  const n = Number(value || 0);
  const d = Number(total || 0);
  if (!d) return 0;
  return Math.max(0, Math.min(100, (n / d) * 100));
}

function SplitBar({ title, items, helper = '' }) {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  return (
    <article className="ops-card" style={{ gridColumn: '1 / -1' }}>
      <div className="ops-card-title ops-loop-card-title">
        <span className="ops-loop-title-left">
          <span>{title}</span>
          <HelperTip text={helper} />
        </span>
      </div>
      <div style={{ display: 'flex', width: '100%', height: 22, borderRadius: 8, overflow: 'hidden', marginTop: 12, background: '#e7f0ee' }}>
        {items.map((item) => {
          const width = pct(item.value, total);
          if (width <= 0) return null;
          return (
            <div
              key={item.key}
              title={`${item.label}: ${formatPercent(width)}`}
              style={{ width: `${width}%`, minWidth: width > 0 ? 3 : 0, background: item.color }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
        {items.map((item) => (
          <span key={item.key} className="ops-card-caption" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 0 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: item.color, display: 'inline-block' }} />
            {item.label}: {item.format ? item.format(item.value) : formatCount(item.value)} ({formatPercent(pct(item.value, total))})
          </span>
        ))}
      </div>
    </article>
  );
}

function TrendLine({ points }) {
  const wrapRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [plotWidth, setPlotWidth] = useState(960);
  const safePoints = Array.isArray(points) ? points : [];
  const values = safePoints
    .map((point) => Number(point?.captureRatePercent))
    .filter((value) => Number.isFinite(value));

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width || 0);
      if (nextWidth > 0) setPlotWidth(Math.max(560, nextWidth));
    };
    updateWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!safePoints.length || !values.length) {
    return <div className="ops-empty-state">Insufficient data for a capture-rate trend.</div>;
  }

  const width = plotWidth;
  const height = 170;
  const paddingLeft = 54;
  const paddingRight = 20;
  const paddingTop = 18;
  const paddingBottom = 34;
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const minValue = Math.max(0, Math.floor(rawMin / 10) * 10);
  const maxValue = Math.min(100, Math.max(10, Math.ceil(rawMax / 10) * 10));
  const range = maxValue - minValue || 1;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const stepX = safePoints.length > 1 ? chartWidth / (safePoints.length - 1) : 0;
  const getPointPosition = (point, index) => {
    const value = Number(point?.captureRatePercent || 0);
    const x = paddingLeft + (stepX * index);
    const y = paddingTop + chartHeight - (((value - minValue) / range) * chartHeight);
    return { x, y, value };
  };
  const polyline = safePoints.map((point, index) => {
    const { x, y } = getPointPosition(point, index);
    return `${x},${y}`;
  }).join(' ');
  const yTicks = Array.from(new Set([minValue, Math.round((minValue + maxValue) / 2), maxValue]));
  const firstLabel = safePoints[0]?.label || '';
  const lastLabel = safePoints[safePoints.length - 1]?.label || '';
  const activePoint = activeIndex === null ? null : safePoints[activeIndex];
  const activePos = activePoint ? getPointPosition(activePoint, activeIndex) : null;

  return (
    <div className="ops-loop-trend-wrap" ref={wrapRef}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="170" role="img" aria-label="Capture-rate trend">
      <text x="0" y="8" className="ops-loop-chart-axis-label">Capture rate</text>
      {yTicks.map((tick) => {
        const y = paddingTop + chartHeight - (((tick - minValue) / range) * chartHeight);
        return (
          <g key={tick}>
            <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="rgba(22,61,74,0.10)" strokeWidth="1" />
            <text x={paddingLeft - 8} y={y + 4} textAnchor="end" className="ops-loop-chart-tick">{formatPercent(tick)}</text>
          </g>
        );
      })}
      <line x1={paddingLeft} y1={height - paddingBottom} x2={width - paddingRight} y2={height - paddingBottom} stroke="rgba(22,61,74,0.18)" strokeWidth="1" />
      <text x={paddingLeft} y={height - 8} textAnchor="middle" className="ops-loop-chart-tick">{firstLabel}</text>
      <text x={width - paddingRight} y={height - 8} textAnchor="middle" className="ops-loop-chart-tick">{lastLabel}</text>
      <polyline
        fill="none"
        stroke="#1f6f78"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={polyline}
      />
      {safePoints.map((point, index) => {
        const { x, y, value } = getPointPosition(point, index);
        const label = `${point?.label || `Point ${index + 1}`}: ${formatPercent(value)} capture rate, ${formatCount(point?.capturedTransactions)} captured of ${formatCount(point?.totalTransactions)} transactions`;
        return (
          <g key={`${point?.label || index}_${index}`}>
            <circle cx={x} cy={y} r={activeIndex === index ? '5.5' : '3.8'} fill="#1f6f78" />
            <circle
              cx={x}
              cy={y}
              r="10"
              fill="transparent"
              tabIndex="0"
              role="button"
              aria-label={label}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
            >
              <title>{label}</title>
            </circle>
          </g>
        );
      })}
      </svg>
      {activePoint && activePos ? (
        <div
          className="ops-loop-chart-tooltip"
          style={{ left: `${(activePos.x / width) * 100}%`, top: `${(activePos.y / height) * 100}%` }}
        >
          <strong>{activePoint.label}</strong>
          <span>{formatPercent(activePos.value)} capture rate</span>
          <span>{formatCount(activePoint.capturedTransactions)} of {formatCount(activePoint.totalTransactions)} txns captured</span>
        </div>
      ) : null}
    </div>
  );
}

const DEFAULT_FILTERS = {
  // YTD, not 30d: k=3 is counted within the window, so a short window suppresses
  // merchants that only clear the threshold over a longer period. Sparse post-link
  // spend makes 30d read empty even when real targets exist.
  window: 'ytd',
  market: 'all',
  state: 'all',
  causeCategory: '',
  segment: 'all',
  includeCandidates: false,
};

export default function OpsLoopHealth() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [merchantView, setMerchantView] = useState('priority');
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [selectedMerchantRow, setSelectedMerchantRow] = useState(null);
  const [permissions, setPermissions] = useState({
    canViewCoverageIntelligence: false,
    canViewStoreLevelDetail: false,
    canExportCoverageData: false,
    canManageCoveragePipeline: false,
  });
  // Optimistic per-store pipeline status overrides, keyed by locationId.
  const [pipelineOverrides, setPipelineOverrides] = useState({});
  const [pipelineBusy, setPipelineBusy] = useState({});

  const ACTIVE_PIPELINE_STATUSES = ['targeted', 'contacted', 'in_conversation', 'committed', 'live'];
  function storeStatus(store) {
    return (store.locationId && pipelineOverrides[store.locationId]) || store.pipelineStatus || 'not_contacted';
  }
  function storeIsTargeted(store) {
    return ACTIVE_PIPELINE_STATUSES.includes(storeStatus(store));
  }

  // G3 "Add to target": idempotent store-level pipeline write, keyed on locationId.
  async function toggleTarget(store) {
    const locId = store.locationId;
    if (!locId || pipelineBusy[locId]) return;
    const nextStatus = storeIsTargeted(store) ? 'not_contacted' : 'targeted';
    setPipelineBusy((b) => ({ ...b, [locId]: true }));
    try {
      const resp = await apiFetch('/ops/program-health/coverage-pipeline', {
        method: 'POST',
        body: {
          locationId: locId,
          merchantEntityId: selectedMerchantRow.merchantEntityId,
          marketKey: store.marketKey || filters.market,
          merchantDisplayName: selectedMerchantRow.displayName || selectedMerchantRow.normalizedMerchant,
          pipelineStatus: nextStatus,
          storeLabel: store.label,
          address: store.address,
          city: store.city,
          state: store.state,
          postalCode: store.postalCode,
          category: selectedMerchantRow.category,
          frequencyType: selectedMerchantRow.frequencyType,
          ownershipTier: selectedMerchantRow.ownershipTier,
          capturableGmvCents: store.gmvCents,
          distinctConsumers: store.distinctConsumers,
        },
      });
      setPipelineOverrides((o) => ({ ...o, [locId]: resp?.record?.pipelineStatus || nextStatus }));
    } catch (err) {
      setError(err?.message || 'Failed to update target');
    } finally {
      setPipelineBusy((b) => ({ ...b, [locId]: false }));
    }
  }

  async function loadLoopHealth(activeFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('window', activeFilters.window || 'ytd');
      params.set('market', activeFilters.market || 'all');
      params.set('state', activeFilters.state || 'all');
      params.set('segment', activeFilters.segment || 'all');
      params.set('includeCandidates', activeFilters.includeCandidates ? 'true' : 'false');
      if (activeFilters.causeCategory) params.set('causeCategory', activeFilters.causeCategory);

      const resp = await apiFetch(`/ops/program-health/coverage-spend-leakage?${params.toString()}`);
      setData(resp || null);
    } catch (err) {
      const status = err?.status;
      if (status === 401) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      if (status === 403) {
        setData(null);
        setError('You do not have permission to view Loop Health coverage intelligence.');
        return;
      }
      setData(null);
      setError(err?.data?.error || err?.message || 'Failed to load loop health metrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLoopHealth(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    async function loadPermissions() {
      try {
        const resp = await apiFetch('/ops/me');
        if (cancelled) return;
        const next = resp?.internalOpsMembership?.permissions || {};
        setPermissions({
          canViewCoverageIntelligence: !!next.canViewCoverageIntelligence,
          canViewStoreLevelDetail: !!next.canViewStoreLevelDetail,
          canExportCoverageData: !!next.canExportCoverageData,
          canManageCoveragePipeline: !!next.canManageCoveragePipeline,
        });
      } catch (_err) {
        if (!cancelled) {
          setPermissions({
            canViewCoverageIntelligence: false,
            canViewStoreLevelDetail: false,
            canExportCoverageData: false,
            canManageCoveragePipeline: false,
          });
        }
      }
    }
    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!merchantModalOpen && !selectedMerchantRow) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMerchantModalOpen(false);
        setSelectedMerchantRow(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [merchantModalOpen, selectedMerchantRow]);

  const metrics = data?.metrics || {};
  const split = metrics.split || {};
  const buckets = split.buckets || {};
  const outOfNetworkOwnership = split.outOfNetworkOwnership || {};
  const topCategories = Array.isArray(metrics.topOutOfNetworkCategories) ? metrics.topOutOfNetworkCategories : [];
  const merchantTargeting = metrics.merchantTargeting || {};
  const topMerchants = Array.isArray(merchantTargeting.topMerchants) ? merchantTargeting.topMerchants : [];
  const inTargetPolygonMerchants = Array.isArray(merchantTargeting.inTargetPolygonMerchants)
    ? merchantTargeting.inTargetPolygonMerchants
    : [];
  const locationAssignment = merchantTargeting.locationAssignment || {};
  const highValueMarketNotAssignedMerchants = Array.isArray(merchantTargeting.highValueMarketNotAssignedMerchants)
    ? merchantTargeting.highValueMarketNotAssignedMerchants
    : [];
  const diagnostics = metrics.diagnostics || {};
  const missedReasons = diagnostics.inNetworkMissedReasons || {};
  const captureDistribution = diagnostics.captureDistribution || {};
  const fragility = diagnostics.fragility || {};
  const nodeHealth = diagnostics.nodeHealth || {};
  const missedPrimaryReasons = Array.isArray(missedReasons.primaryReasons) ? missedReasons.primaryReasons : [];
  const captureHistogram = Array.isArray(captureDistribution.histogram) ? captureDistribution.histogram : [];
  const nfpRows = Array.isArray(nodeHealth.nfps?.rows) ? nodeHealth.nfps.rows : [];
  const integrity = metrics.integrity || {};
  const statuses = metrics.statuses || {};
  const portfolio = metrics.portfolio || {};
  const signedNetworkMix = Array.isArray(portfolio.signedNetworkMix) ? portfolio.signedNetworkMix : [];
  const addressableSpendMix = Array.isArray(portfolio.addressableSpendMix) ? portfolio.addressableSpendMix : [];
  const ownershipFrequencyGrid = portfolio.ownershipFrequencyGrid || {};
  const ownershipFrequencyCells = Array.isArray(ownershipFrequencyGrid.cells) ? ownershipFrequencyGrid.cells : [];
  const ownershipTiers = Array.isArray(ownershipFrequencyGrid.ownershipTiers)
    ? ownershipFrequencyGrid.ownershipTiers
    : ['local', 'regional', 'national'];
  const frequencyTypes = Array.isArray(ownershipFrequencyGrid.frequencyTypes)
    ? ownershipFrequencyGrid.frequencyTypes
    : ['everyday', 'periodic', 'treat'];
  const portfolioDiagnostics = portfolio.diagnostics || {};
  const asOfTimestamp = data?.materializedRollup?.generatedAt || data?.window?.end;
  const rollupSource = formatStateLabel(data?.materializedRollup?.source);
  const hasSpecificMarket = !!filters.market && filters.market !== 'all';
  const marketScopedTopMerchants = hasSpecificMarket
    ? topMerchants.filter((row) => row.inTargetPolygon === true)
    : topMerchants;
  const priorityMerchants = marketScopedTopMerchants.filter((row) => (
    row.ownershipTier === 'local' && row.classificationStatus === 'confirmed'
  ));
  const reviewMerchants = marketScopedTopMerchants.filter((row) => row.classificationStatus === 'needs_review');
  const nationalContextMerchants = marketScopedTopMerchants.filter((row) => (
    row.ownershipTier === 'national' || row.ownershipTier === 'regional'
  ));
  const visibleMerchantRows = merchantView === 'priority'
    ? priorityMerchants
    : merchantView === 'review'
      ? reviewMerchants
      : nationalContextMerchants;
  const firstScreenMerchantRows = visibleMerchantRows.slice(0, 5);
  const hiddenMerchantCount = Math.max(0, visibleMerchantRows.length - firstScreenMerchantRows.length);

  const splitItemsGmv = [
    { key: 'captured', label: 'Captured', value: buckets.captured?.gmvCents, color: '#1f6f78', format: formatMoney },
    { key: 'missed', label: 'In-network missed', value: buckets.inNetworkMissed?.gmvCents, color: '#eba477', format: formatMoney },
    { key: 'local', label: 'Out: local', value: outOfNetworkOwnership.local?.gmvCents, color: '#2e9f74', format: formatMoney },
    { key: 'regional', label: 'Out: regional', value: outOfNetworkOwnership.regional?.gmvCents, color: '#7aa9b3', format: formatMoney },
    { key: 'national', label: 'Out: national', value: outOfNetworkOwnership.national?.gmvCents, color: '#b4a0c9', format: formatMoney },
    { key: 'excluded', label: 'Out: excluded', value: outOfNetworkOwnership.excluded?.gmvCents, color: '#b8c7ca', format: formatMoney },
  ];

  async function exportCoverage(type) {
    const params = new URLSearchParams();
    params.set('type', type);
    params.set('window', filters.window || 'ytd');
    params.set('market', filters.market || 'all');
    params.set('state', filters.state || 'all');
    params.set('segment', filters.segment || 'all');
    params.set('includeCandidates', filters.includeCandidates ? 'true' : 'false');
    if (filters.causeCategory) params.set('causeCategory', filters.causeCategory);
    await apiDownload(`/ops/program-health/coverage-spend-leakage/export?${params.toString()}`, {
      filename: type === 'categories'
        ? 'loop-health-out-of-network-categories.csv'
        : 'loop-health-export-eligible-merchants.csv',
    });
  }

  return (
    <OpsLayout
      title="Loop Health"
      subtitle="Where the loop is leaking, and the ranked list of who to sign to close it."
    >
      <div className="ops-nfp-content ops-loop-redesign">
        <section className="ops-loop-filter-card" style={{ marginTop: 0 }}>
          <div className="ops-loop-filter-topline">
            <div>
              <span className="ops-loop-eyebrow">Coverage command view</span>
              <p>Filter the wallet slice, then work from capture health to acquisition targets.</p>
            </div>
            {data ? (
              <span className="ops-loop-asof-pill">
                As of {formatDateTime(asOfTimestamp)} · {rollupSource}
              </span>
            ) : null}
          </div>
          <div className="ops-loop-filter-grid">
            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Window</span>
              <select
                value={filters.window}
                onChange={(e) => setFilters((prev) => ({ ...prev, window: e.target.value }))}
              >
                <option value="30d">Last 30 Days</option>
                <option value="60d">Last 60 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="ytd">YTD</option>
                <option value="all">All Time</option>
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Market</span>
              <select
                value={filters.market}
                onChange={(e) => setFilters((prev) => ({ ...prev, market: e.target.value }))}
              >
                {(Array.isArray(data?.filters?.markets) ? data.filters.markets : [{ key: 'all', label: 'All Markets' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>State</span>
              <select
                value={filters.state}
                onChange={(e) => setFilters((prev) => ({ ...prev, state: e.target.value, market: 'all' }))}
              >
                {(Array.isArray(data?.filters?.states) ? data.filters.states : [{ key: 'all', label: 'All States' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Cause Category</span>
              <select
                value={filters.causeCategory}
                onChange={(e) => setFilters((prev) => ({ ...prev, causeCategory: e.target.value }))}
              >
                <option value="">All Categories</option>
                {(Array.isArray(data?.filters?.causeCategories) ? data.filters.causeCategories : []).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Consumer Segment</span>
              <select
                value={filters.segment}
                onChange={(e) => setFilters((prev) => ({ ...prev, segment: e.target.value }))}
              >
                {(Array.isArray(data?.filters?.segments) ? data.filters.segments : [{ key: 'all', label: 'All' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-market-checkbox" style={{ alignSelf: 'end', paddingBottom: 8 }}>
              <input
                type="checkbox"
                checked={filters.includeCandidates}
                onChange={(e) => setFilters((prev) => ({ ...prev, includeCandidates: e.target.checked }))}
              />
              Include candidates
            </label>
          </div>

          <div className="ops-loop-filter-note">
            {data?.glossary || 'Analyzable observed = posted transactions from linked consumers surviving the exclusion filter.'}
            {filters.includeCandidates ? ' Candidate home areas are included in market-scoped results.' : ''}
          </div>
          {data?.privacy ? (
            <div className="ops-loop-filter-note">
              Aggregate-only API. k-anonymity: {data.privacy.kAnonymity}. Suppressed category rows: {formatCount(data.privacy.suppressedCategoryRows)}.
              {' '}<StatusPill status={statuses.privacyContract} />
            </div>
          ) : null}
        </section>

        {error ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state" style={{ color: '#b74d18' }}>{error}</div>
          </section>
        ) : null}

        {loading ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state">Loading loop health metrics...</div>
          </section>
        ) : (
          <>
            <div className="ops-loop-act-label"><span>Act first</span> The one thing this page exists to answer</div>
            <section className="ops-loop-act-grid">
              <article className="ops-loop-command-card primary">
                <div className="ops-loop-command-label">Addressable out-of-network GMV</div>
                <div className="ops-loop-command-value">{formatMoney(metrics.addressableOutOfNetworkGmvCents)}</div>
                <p>Local spend by linked consumers at businesses not yet in network. This is the loop you can close by signing merchants.</p>
                <div className="ops-loop-command-list">
                  {topCategories.slice(0, 3).map((row) => (
                    <span key={row.category}>
                      <em>{row.label} · {formatOwnershipTier(row.ownershipTier)}</em>
                      <strong>{formatMoney(row.gmvCents)}</strong>
                    </span>
                  ))}
                </div>
              </article>

              <article className="ops-loop-command-card ops-loop-merchant-command">
                <div className="ops-loop-section-header">
                  <div>
                    <h2>Close the loop next</h2>
                  </div>
                  <div className="ops-loop-section-actions">
                    {visibleMerchantRows.length ? (
                      <button
                        type="button"
                        className="ops-btn"
                        onClick={() => setMerchantModalOpen(true)}
                      >
                        View all {formatCount(visibleMerchantRows.length)}
                      </button>
                    ) : null}
                    {permissions.canExportCoverageData ? (
                      <button
                        type="button"
                        className="ops-btn primary"
                        onClick={() => exportCoverage('merchants')}
                        disabled={!topMerchants.some((row) => row.exportEligible)}
                      >
                        Export eligible
                      </button>
                    ) : null}
                  </div>
                </div>
	                <p className="ops-action-queue-subtitle">
	                  {hasSpecificMarket
	                    ? 'Selected-market views show only merchants with explicitly assigned in-market store geography.'
	                    : 'All-market views are portfolio-wide and are not scoped to one market store list.'}
	                </p>
	                <div className="ops-loop-target-grid">
	                  <MetricCard
	                    title="Merchant Geo Completeness"
	                    value={formatPercent(merchantTargeting.merchantGeographyCompletenessPercent)}
	                    caption="Merchant rows with usable geography"
	                    helper="Gate 1: whether the merchant catalog has enough location data to attempt market resolution."
	                  />
	                  <MetricCard
	                    title="Market Assignment"
	                    value={formatPercent(locationAssignment.marketAssignmentCompletenessPercent)}
	                    caption={`${formatCount(locationAssignment.marketAssignedLocationCount)} assigned · ${formatCount(locationAssignment.marketNotAssignedLocationCount)} not assigned`}
	                    helper="Gate 2: processed geo locations assigned to an existing canonical market. Out-of-footprint locations are not counted as fixable."
	                  />
	                  <MetricCard
	                    title="Resolution Pending"
	                    value={formatPercent(locationAssignment.pendingResolutionPercent)}
	                    caption={`${formatCount(locationAssignment.resolutionPendingLocationCount)} locations awaiting async resolution`}
	                    helper="Usable geography exists, but the async resolver/backfill has not finished yet."
	                  />
	                </div>
	                <div className="ops-loop-tabs" role="tablist" aria-label="Merchant targeting views">
                  <button type="button" className={merchantView === 'priority' ? 'active' : ''} onClick={() => setMerchantView('priority')}>
                    Recruitable now {formatCount(priorityMerchants.length)}
                  </button>
                  <button type="button" className={merchantView === 'review' ? 'active' : ''} onClick={() => setMerchantView('review')}>
                    Needs review {formatCount(reviewMerchants.length)}
                  </button>
                  <button type="button" className={merchantView === 'context' ? 'active' : ''} onClick={() => setMerchantView('context')}>
                    National context {formatCount(nationalContextMerchants.length)}
                  </button>
                </div>
                <div className="ops-queue-table-wrap ops-loop-priority-table">
                  <table className="ops-queue-table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Category</th>
                        <th>Frequency</th>
                        <th>Capturable GMV</th>
                        <th>Consumers</th>
                        <th>Location</th>
                        <th>Market</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {firstScreenMerchantRows.length ? firstScreenMerchantRows.map((row) => (
                        <tr key={`${merchantView}_${row.merchantRefId}`}>
	                          <td>
	                            <MerchantNameButton row={row} onOpen={setSelectedMerchantRow} />
	                            <small>
	                              {formatOwnershipTier(row.ownershipTier)} · {formatStateLabel(row.classificationStatus)}
	                              {row.storeAttribution?.resolvedStoreCount ? ` · ${formatCount(row.storeAttribution.resolvedStoreCount)} stores` : ''}
                            </small>
                          </td>
                          <td>{row.categoryLabel}</td>
                          <td>
                            <span className={`ops-loop-frequency-pill ${row.frequencyType || 'periodic'}`}>
                              <span className="ops-loop-frequency-icon" aria-hidden="true">{frequencyIcon(row.frequencyType)}</span>
                              {formatFrequencyType(row.frequencyType)}
                            </span>
                          </td>
                          <td>{formatMoney(row.gmvCents)}</td>
                          <td>{formatCount(row.distinctConsumers)}</td>
                          <td>
                            <MerchantLocationCell row={row} />
                          </td>
                          <td>
                            <MerchantMarketCell row={row} hasSpecificMarket={hasSpecificMarket} />
                          </td>
                          <td>
                            <button type="button" className="ops-btn" onClick={() => setSelectedMerchantRow(row)}>
                              Details
                            </button>
                            <small>{row.exportEligible ? 'Eligible' : formatStateLabel(row.pipelineStatus)}</small>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="8">
                            {hasSpecificMarket
                              ? 'No merchant rows in this view meet the selected-market privacy threshold. Market totals can still include suppressed aggregate spend.'
                              : 'No merchant rows in this view for the current slice.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {hiddenMerchantCount ? (
                  <button
                    type="button"
                    className="ops-loop-view-more"
                    onClick={() => setMerchantModalOpen(true)}
                  >
                    View {formatCount(hiddenMerchantCount)} more in this view
                  </button>
                ) : null}
              </article>
            </section>
            <div className="ops-loop-why-strip">
              <strong>Why these:</strong> confirmed classification, non-excluded ownership, privacy-safe consumers, sorted by capturable spend.
	              <span>{formatCount(merchantTargeting.suppressed?.belowK)} rows suppressed below k-anonymity · merchant geo {formatPercent(merchantTargeting.merchantGeographyCompletenessPercent)} complete</span>
	              {highValueMarketNotAssignedMerchants.length ? (
	                <span>{formatCount(highValueMarketNotAssignedMerchants.length)} high-value market-not-assigned merchants need resolver review</span>
	              ) : null}
	            </div>

            <section className="ops-loop-section">
              <div className="ops-loop-section-header">
                <div>
                  <span className="ops-loop-eyebrow">Spend map</span>
                  <h2>Where every dollar goes</h2>
                </div>
              </div>
              <p className="ops-action-queue-subtitle">
                Green is won, orange is ops recovery, teal is the BD surface. {hasSpecificMarket
                  ? <>Market-observed consumers: {formatCount(metrics.marketObservedConsumers)} · cohort linked consumers: {formatCount(metrics.scopedLinkedConsumers)}.</>
                  : <>Scoped linked consumers: {formatCount(metrics.scopedLinkedConsumers)}.</>}
              </p>
              <div className="ops-loop-bucket-grid">
                <MetricCard
                  title="1 - Captured"
                  value={formatMoney(buckets.captured?.gmvCents)}
                  caption={`${formatCount(buckets.captured?.count)} txns. Loop closed.`}
                  helper="Transactions that generated a contribution and closed the Loop."
                />
                <MetricCard
                  title="2 - In-Network Missed"
                  value={formatMoney(buckets.inNetworkMissed?.gmvCents)}
                  caption={`${formatCount(buckets.inNetworkMissed?.count)} txns. Ops recovery surface.`}
                  helper="Transactions matched to an in-network business but missed contribution capture."
                />
                <MetricCard
                  title="3 - Out-of-Network"
                  value={formatMoney(buckets.outOfNetwork?.gmvCents)}
                  caption={`${formatCount(buckets.outOfNetwork?.count)} txns. BD coverage surface.`}
                  helper="Analyzable transactions at businesses not currently in the Loop network."
                />
              </div>
              <div className="ops-cards ops-loop-split-cards">
                <SplitBar
                  title="By GMV"
                  items={splitItemsGmv}
                  helper="Spend split across captured, missed, and ownership/addressability classes."
                />
              </div>
              <div className="ops-loop-mini-metrics">
                <span>Network capture rate <strong>{formatPercent(metrics.networkCaptureRatePercent)}</strong></span>
                <span>Contribution yield <strong>{formatBps(metrics.contributionYieldBps)}</strong></span>
                <span>Total observed GMV <strong>{formatMoney(metrics.totalObservedGmvCents)}</strong></span>
              </div>
            </section>

            <section className="ops-loop-section">
              <div className="ops-loop-section-header">
                <div>
                  <span className="ops-loop-eyebrow">Portfolio position</span>
                  <h2>Where the addressable spend lives</h2>
                </div>
                <HelperTip text="Ownership mix is a directional portfolio signal, not a hard quota. Frequency is classified from merchant/category properties, not consumer behavior." />
              </div>
              <div className="ops-loop-portfolio-grid">
                <MetricCard
                  title="Independent-Addressable Spend"
                  value={formatPercent(portfolioDiagnostics.independentAddressableSharePercent)}
                  caption={`${formatMoney(portfolioDiagnostics.independentAddressableGmvCents)} local-addressable analyzable GMV`}
                  helper="Share of analyzable spend classified as local independent-addressable under the current portfolio rules."
                />
                <MetricCard
                  title="Everyday Local Share"
                  value={formatPercent(portfolioDiagnostics.everydayIndependentAddressableSharePercent)}
                  caption={`${formatMoney(portfolioDiagnostics.everydayIndependentAddressableGmvCents)} local everyday GMV`}
                  helper="Share of everyday-frequency analyzable spend that is local-addressable. This is the frequency ceiling diagnostic."
                />
                <MetricCard
                  title="Portfolio Phase"
                  value={formatStateLabel(portfolio.phase)}
                  caption="Target mix is directional and config-driven"
                  helper="Market phase used to select the local/regional/national target mix hypothesis."
                />
              </div>
              <div className="ops-loop-mix-grid">
                {ownershipTiers.map((tier) => {
                  const signed = signedNetworkMix.find((row) => row.ownershipTier === tier) || {};
                  const addressable = addressableSpendMix.find((row) => row.ownershipTier === tier) || {};
                  return (
                    <article key={tier} className="ops-loop-mix-card">
                      <span>{formatOwnershipTier(tier)}</span>
                      <strong>{formatPercent(addressable.gmvSharePercent)}</strong>
                      <small>{formatMoney(addressable.gmvCents)} addressable · signed {formatPercent(signed.gmvSharePercent)}</small>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="ops-loop-section">
              <div className="ops-loop-section-heading">
                <h2>Capture-Rate Trend</h2>
                <HelperTip text="Daily capture rate is captured transactions divided by analyzable posted transactions for each day in the selected window." />
              </div>
              {metrics.captureTrend?.status === 'insufficient_data' ? (
                <div className="ops-empty-state" style={{ marginTop: 12 }}>Insufficient data for a reliable trend.</div>
              ) : (
                <TrendLine points={metrics.captureTrend?.points} />
              )}
            </section>

            <section className="ops-panel">
              <h2>Integrity Status</h2>
              <p className="ops-action-queue-subtitle">
                Traffic-light status appears only where the target is definable.
              </p>
              <div className="ops-cards">
                <MetricCard
                  title="Hard Liabilities"
                  value={formatCount(integrity.hardLiabilitiesCount)}
                  caption="Visible-period unfunded liabilities"
                  status={statuses.hardLiabilities}
                  helper="Transactions visible to consumers while the business wallet was not funded."
                />
                <MetricCard
                  title="Reconciliation Exceptions"
                  value={formatCount(integrity.reconciliationExceptionsCount)}
                  caption="Ledger/source-of-truth mismatches"
                  status={statuses.reconciliationExceptions}
                  helper="Settlement or ledger rows that do not reconcile to expected financial state."
                />
                <MetricCard
                  title="Privacy Contract"
                  value={statuses.privacyContract ? (statuses.privacyContract.key === 'good' ? 'Passing' : 'Breach') : '--'}
                  caption="Aggregate-only response and k-anonymity guard"
                  status={statuses.privacyContract}
                  helper="Confirms this endpoint is returning aggregate metrics and meeting the k-anonymity floor."
                />
              </div>
            </section>

            <section className="ops-panel">
              <h2>Ownership by Frequency</h2>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Ownership</th>
                      {frequencyTypes.map((frequencyType) => (
                        <th key={frequencyType}>{formatFrequencyType(frequencyType)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ownershipTiers.length ? ownershipTiers.map((tier) => (
                      <tr key={tier}>
                        <td>{formatOwnershipTier(tier)}</td>
                        {frequencyTypes.map((frequencyType) => {
                          const cell = ownershipFrequencyCells.find((row) => (
                            row.ownershipTier === tier && row.frequencyType === frequencyType
                          ));
                          return (
                            <td key={`${tier}_${frequencyType}`}>
                              {cell?.suppressed ? (
                                <span>Suppressed below k</span>
                              ) : (
                                <span>
                                  {formatMoney(cell?.total?.gmvCents)} · {formatCount(cell?.total?.count)} txns · {formatPercent(cell?.coveragePercent)} captured
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={frequencyTypes.length + 1}>No ownership-by-frequency grid data in this slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <h2>In-Network Missed Reasons</h2>
              <p className="ops-action-queue-subtitle">
                Deterministic primary reason counts. Deferred value uses the contribution estimate already carried on matched transactions.
              </p>
              <div className="ops-cards">
                <MetricCard
                  title="Deferred Contribution"
                  value={formatMoney(missedReasons.totalDeferredContributionCents)}
                  caption="Estimated recoverable contribution from in-network missed transactions"
                  helper="Estimated contribution value tied to in-network missed transactions."
                />
                <MetricCard
                  title="Reason Rows"
                  value={formatCount(missedPrimaryReasons.length)}
                  caption="Primary reason buckets in this slice"
                  helper="Number of deterministic primary missed-reason buckets represented below."
                />
              </div>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Primary Reason</th>
                      <th>Txn Count</th>
                      <th>GMV</th>
                      <th>Deferred Contribution</th>
                      <th>Businesses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missedPrimaryReasons.length ? missedPrimaryReasons.map((row) => (
                      <tr key={row.reason}>
                        <td>{formatStateLabel(row.reason)}</td>
                        <td>{formatCount(row.transactionCount)}</td>
                        <td>{formatMoney(row.gmvCents)}</td>
                        <td>{formatMoney(row.deferredContributionCents)}</td>
                        <td>{formatCount(row.distinctBusinesses)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5">No in-network missed diagnostics in this slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <h2>Capture Distribution</h2>
              <div className="ops-cards">
                <MetricCard
                  title="Consumers Observed"
                  value={formatCount(captureDistribution.totalConsumers)}
                  caption="Consumers with analyzable observed transactions in the selected window"
                  helper="Distinct linked consumers with at least one analyzable transaction in this slice."
                />
                <MetricCard
                  title="Zero Capture"
                  value={formatCount(captureDistribution.zeroCaptureCount)}
                  caption="Consumers with analyzable transactions and no captured transaction"
                  helper="Consumers with observed analyzable spend but no captured Loop transaction in this slice."
                />
              </div>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Personal Capture Rate</th>
                      <th>Consumers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {captureHistogram.length ? captureHistogram.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>{formatCount(row.consumerCount)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="2">No capture-distribution data in this slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <h2>Fragility</h2>
              <div className="ops-cards">
                <MetricCard
                  title="Top Consumer Share"
                  value={formatPercent(fragility.topConsumerCapturedShare?.sharePercent)}
                  caption={`${formatCount(fragility.topConsumerCapturedShare?.transactionCount)} captured txns from the top consumer`}
                  status={statuses.fragilityTopConsumerShare}
                  helper="Share of captured transactions attributable to the single highest-capture consumer."
                />
                <MetricCard
                  title="Top Business Share"
                  value={formatPercent(fragility.topBusinessContributionShare?.sharePercent)}
                  caption={`${fragility.topBusinessContributionShare?.displayName || 'No business'} · ${formatMoney(fragility.topBusinessContributionShare?.contributionCents)}`}
                  status={statuses.fragilityTopBusinessShare}
                  helper="Share of captured contribution dollars attributable to the top business."
                />
                <MetricCard
                  title="Top NFP Share"
                  value={formatPercent(fragility.topNfpContributionShare?.sharePercent)}
                  caption={`${fragility.topNfpContributionShare?.displayName || 'No NFP'} · ${formatMoney(fragility.topNfpContributionShare?.contributionCents)}`}
                  status={statuses.fragilityTopNfpShare}
                  helper="Share of captured contribution dollars attributable to the top NFP."
                />
              </div>
            </section>

            <section className="ops-panel">
              <h2>Node Health</h2>
              <div className="ops-cards">
                <MetricCard
                  title="Active Businesses"
                  value={formatCount(nodeHealth.businesses?.active)}
                  caption="Matched businesses visible and active in this slice"
                  helper="Matched businesses currently active and visible to consumers."
                />
                <MetricCard
                  title="Never Funded"
                  value={formatCount(nodeHealth.businesses?.neverFunded)}
                  caption="Matched businesses with no funding history and no wallet balance"
                  status={statuses.bucket2NeverFundedBusinesses}
                  helper="Matched businesses that appear never to have funded their wallet."
                />
                <MetricCard
                  title="Wallet Depleted"
                  value={formatCount(nodeHealth.businesses?.fundedThenDepleted)}
                  caption="Matched businesses with funding history and depleted wallet"
                  status={statuses.bucket2WalletDepletedBusinesses}
                  helper="Matched businesses with funding history but no current wallet balance."
                />
                <MetricCard
                  title="Business Hidden"
                  value={formatCount(nodeHealth.businesses?.hidden)}
                  caption="Matched businesses hidden from consumer visibility"
                  status={statuses.bucket2BusinessHidden}
                  helper="Matched businesses hidden from consumers during this operational slice."
                />
                <MetricCard
                  title="Business Concentration"
                  value={formatPercent(nodeHealth.businesses?.contributionConcentrationPercent)}
                  caption="Top business share of captured contributions"
                  helper="How concentrated captured contribution value is in the top business."
                />
                <MetricCard
                  title="Payout Ready NFPs"
                  value={`${formatCount(nodeHealth.nfps?.payoutReady)} / ${formatCount(nodeHealth.nfps?.total)}`}
                  caption="NFP nodes with payout readiness ready"
                  helper="NFPs in this slice that can currently receive payout."
                />
                <MetricCard
                  title="Visible Not Payout Ready"
                  value={formatCount(nodeHealth.nfps?.visibleButPayoutNotReady)}
                  caption="NFP proof asset risk"
                  status={statuses.nfpPayoutReady}
                  helper="Visible NFPs represented in the slice that are not payout-ready."
                />
              </div>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>NFP</th>
                      <th>Contribution</th>
                      <th>Payout Status</th>
                      <th>Blocker</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfpRows.length ? nfpRows.map((row) => (
                      <tr key={row.displayName}>
                        <td>{row.displayName}</td>
                        <td>{formatMoney(row.contributionCents)}</td>
                        <td>{formatStateLabel(row.payoutReadinessStatus)}</td>
                        <td>{formatStateLabel(row.payoutBlockedReason)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4">No NFP contribution rows in this slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <div className="ops-market-panel-header">
                <h2>Top Out-of-Network Categories</h2>
                {permissions.canExportCoverageData ? (
                  <button type="button" className="ops-btn" onClick={() => exportCoverage('categories')} disabled={!topCategories.length}>
                    Export Categories
                  </button>
                ) : null}
              </div>
              <p className="ops-action-queue-subtitle">
                Non-excluded portfolio categories only. Rows below the privacy threshold are suppressed by the API.
              </p>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Ownership</th>
                      <th>Frequency</th>
                      <th>Txn Count</th>
                      <th>GMV</th>
                      <th>Distinct Consumers</th>
                      <th>In-Network Coverage</th>
                      <th>Opportunity Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCategories.length ? topCategories.map((row) => (
                      <tr key={row.category}>
                        <td>{row.label}</td>
                        <td>{formatOwnershipTier(row.ownershipTier)}</td>
                        <td>{formatFrequencyType(row.frequencyType)}</td>
                        <td>{formatCount(row.transactionCount)}</td>
                        <td>{formatMoney(row.gmvCents)}</td>
                        <td>{formatCount(row.distinctConsumers)}</td>
                        <td>{formatPercent(row.inNetworkCoveragePercent)}</td>
                        <td>{formatCount(row.opportunityScore)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8">No privacy-safe addressable category rows in this slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <div className="ops-market-panel-header">
                <h2>Out-of-Network Merchant Targeting</h2>
                {permissions.canExportCoverageData ? (
                  <button
                    type="button"
                    className="ops-btn"
                    onClick={() => exportCoverage('merchants')}
                    disabled={!topMerchants.some((row) => row.exportEligible)}
                  >
                    Export Eligible Merchants
                  </button>
                ) : null}
              </div>
              <p className="ops-action-queue-subtitle">
                Aggregate-only merchant rows. Export eligibility requires high confidence, non-excluded ownership, and confirmed classification.
              </p>
              <div className="ops-cards">
                <MetricCard
                  title="Merchant Geo Completeness"
                  value={formatPercent(merchantTargeting.merchantGeographyCompletenessPercent)}
                  caption="Share of targetable merchant rows with usable geography"
                  helper="Percent of targetable merchant rows with coordinates, locality, or postal geography."
                />
                <MetricCard
                  title="Suppressed Below k"
                  value={formatCount(merchantTargeting.suppressed?.belowK)}
                  caption="Merchant rows suppressed before API response"
                  helper="Merchant rows withheld because distinct consumer count is below the privacy threshold."
                />
                <MetricCard
                  title="Suppressed Ambiguous/P2P"
                  value={formatCount(merchantTargeting.suppressed?.ambiguousOrP2P)}
                  caption="Rows excluded from targeting due to normalization state"
                  helper="Rows excluded because the merchant identity is ambiguous or likely person-to-person."
                />
              </div>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Category</th>
                      <th>Ownership</th>
                      <th>Status</th>
                      <th>Frequency</th>
                      <th>Txn</th>
                      <th>GMV</th>
                      <th>Consumers</th>
                      <th>Confidence</th>
                      <th>Location</th>
                      <th>Market</th>
                      <th>Pipeline</th>
                      <th>Export</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topMerchants.length ? topMerchants.map((row) => (
                      <tr key={row.merchantRefId}>
                        <td>
	                          <MerchantNameButton row={row} onOpen={setSelectedMerchantRow} />
                        </td>
                        <td>{row.categoryLabel}</td>
                        <td>{formatOwnershipTier(row.ownershipTier)}</td>
                        <td>{formatStateLabel(row.classificationStatus)}</td>
                        <td>
                          <span className={`ops-loop-frequency-pill ${row.frequencyType || 'periodic'}`}>
                            <span className="ops-loop-frequency-icon" aria-hidden="true">{frequencyIcon(row.frequencyType)}</span>
                            {formatFrequencyType(row.frequencyType)}
                          </span>
                        </td>
                        <td>{formatCount(row.transactionCount)}</td>
                        <td>{formatMoney(row.gmvCents)}</td>
                        <td>{formatCount(row.distinctConsumers)}</td>
                        <td>{formatStateLabel(row.normalizationState)}</td>
                        <td>
                          <MerchantLocationCell row={row} />
                        </td>
                        <td>
                          <MerchantMarketCell row={row} hasSpecificMarket={hasSpecificMarket} />
                        </td>
                        <td>{formatStateLabel(row.pipelineStatus)}</td>
                        <td>{row.exportEligible ? 'Eligible' : 'No'}</td>
                        <td>
                          <button type="button" className="ops-btn" onClick={() => setSelectedMerchantRow(row)}>
                            Details
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="14">
                          {hasSpecificMarket
                            ? 'No selected-market merchant targets meet the privacy threshold. Market totals can still include suppressed aggregate spend.'
                            : 'No privacy-safe merchant targets in this slice.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ops-panel">
              <h2>Merchants Inside Target Market</h2>
              <p className="ops-action-queue-subtitle">
                Requires a specific market and merchant geography. All rows are aggregate-only and privacy-thresholded.
              </p>
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Category</th>
                      <th>Txn</th>
                      <th>GMV</th>
                      <th>Consumers</th>
                      <th>Confidence</th>
                      <th>Location</th>
                      <th>Market</th>
                      <th>Export</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inTargetPolygonMerchants.length ? inTargetPolygonMerchants.map((row) => (
                      <tr key={row.merchantRefId}>
	                        <td>
	                          <MerchantNameButton row={row} onOpen={setSelectedMerchantRow} />
	                        </td>
                        <td>{row.categoryLabel}</td>
                        <td>{formatCount(row.transactionCount)}</td>
                        <td>{formatMoney(row.gmvCents)}</td>
                        <td>{formatCount(row.distinctConsumers)}</td>
                        <td>{formatStateLabel(row.normalizationState)}</td>
                        <td><MerchantLocationCell row={row} /></td>
                        <td><MerchantMarketCell row={row} hasSpecificMarket={hasSpecificMarket} /></td>
                        <td>{row.exportEligible ? 'Eligible' : 'No'}</td>
                        <td>
                          <button type="button" className="ops-btn" onClick={() => setSelectedMerchantRow(row)}>
                            Details
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="10">
                          {hasSpecificMarket
                            ? 'No in-target merchant rows meet the selected-market privacy threshold.'
                            : 'No in-target merchant rows for this slice.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {selectedMerchantRow ? (
          <div
            className="ops-loop-detail-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectedMerchantRow(null);
            }}
          >
            <aside className="ops-loop-detail-panel" role="dialog" aria-modal="true" aria-labelledby="loop-merchant-detail-title">
              <div className="ops-loop-detail-hero">
                <div className="ops-loop-detail-badges">
                  <span>{formatOwnershipTier(selectedMerchantRow.ownershipTier)} ownership</span>
                  <span>{formatStateLabel(selectedMerchantRow.classificationStatus)}</span>
                  <span>{formatFrequencyType(selectedMerchantRow.frequencyType)}</span>
                </div>
                <button type="button" className="ops-loop-detail-close" onClick={() => setSelectedMerchantRow(null)} aria-label="Close merchant detail">
                  x
                </button>
                <h2 id="loop-merchant-detail-title">{selectedMerchantRow.displayName || selectedMerchantRow.normalizedMerchant}</h2>
                <p>{selectedMerchantRow.categoryLabel} · Out-of-network merchant rollup</p>
                <div className="ops-loop-detail-stats">
                  <span>
                    <strong>{formatMoney(selectedMerchantRow.gmvCents)}</strong>
                    Capturable GMV
                  </span>
                  <span>
                    <strong>{formatCount(selectedMerchantRow.distinctConsumers)}</strong>
                    Distinct consumers
                  </span>
                  <span>
                    <strong>{formatCount(selectedMerchantRow.storeAttribution?.resolvedStoreCount || 0)}</strong>
                    Resolved stores
                  </span>
                </div>
              </div>
              <div className="ops-loop-detail-body">
                <div className="ops-loop-detail-summary">
                  <div className="ops-loop-detail-progress">
                    <span style={{ width: `${Number(selectedMerchantRow.storeAttribution?.attributionRatePercent || 0)}%` }} />
                  </div>
                  <div className="ops-loop-detail-legend">
                    <span><i /> {formatMoney(selectedMerchantRow.storeAttribution?.resolvedStoreGmvCents)} resolved to stores</span>
                    <span><i className="muted" /> {formatMoney(selectedMerchantRow.storeAttribution?.notItemizedGmvCents)} unresolved / suppressed</span>
                  </div>
                  <p>
                    {formatMoney(selectedMerchantRow.storeAttribution?.resolvedStoreGmvCents)} across {formatCount(selectedMerchantRow.storeAttribution?.resolvedStoreCount)} resolved stores. {formatMoney(selectedMerchantRow.storeAttribution?.notItemizedGmvCents)} is not tied to an itemized store because geography is missing or the store is below k-anonymity.
                  </p>
                </div>

                <h3>
                  Resolved Storefronts
                  {Number(selectedMerchantRow.storeAttribution?.resolvedStoreCount || 0) >
                    Number(selectedMerchantRow.storeAttribution?.itemizedStoreCount || 0)
                    ? ` (showing top ${formatCount(selectedMerchantRow.storeAttribution?.itemizedStoreCount)} of ${formatCount(selectedMerchantRow.storeAttribution?.resolvedStoreCount)})`
                    : ''}
                </h3>
                <div className="ops-loop-store-list">
                  {merchantStorefronts(selectedMerchantRow).length ? merchantStorefronts(selectedMerchantRow).map((store) => (
                    <article key={store.storeKey || store.label} className="ops-loop-store-card">
                      <div>
                        <strong>{selectedMerchantRow.displayName || selectedMerchantRow.normalizedMerchant}</strong>
                        <span>{store.label}</span>
                      </div>
	                      <div className="ops-loop-store-card-metrics">
	                        <strong>{formatMoney(store.gmvCents)}</strong>
	                        <span>{formatCount(store.distinctConsumers)} consumers</span>
	                        <em>{formatMarketAttribution({ inTargetPolygon: store.inTargetMarket, marketLabel: store.marketLabel }, hasSpecificMarket)}</em>
	                        <span>{formatLocationAssignmentStatus(store.marketAssignmentStatus)}</span>
	                      </div>
	                      {permissions.canManageCoveragePipeline && store.locationId ? (
	                        <button
	                          type="button"
	                          className={storeIsTargeted(store) ? 'ops-loop-target-btn active' : 'ops-loop-target-btn'}
	                          disabled={!!pipelineBusy[store.locationId]}
	                          onClick={() => toggleTarget(store)}
	                        >
	                          {pipelineBusy[store.locationId]
	                            ? 'Saving…'
	                            : storeIsTargeted(store)
	                              ? `Added · ${formatStateLabel(storeStatus(store))} — Remove`
	                              : '+ Add to target'}
	                        </button>
	                      ) : null}
                    </article>
                  )) : (
                    <div className="ops-loop-detail-empty">
                      No storefront passes itemization for this rollup. Store rows require usable transaction location evidence and at least k distinct consumers.
                    </div>
                  )}
                </div>

                <h3>Accounted For, Not Itemizable</h3>
                <div className="ops-loop-detail-note">
                  <strong>{formatCount(selectedMerchantRow.storeAttribution?.belowKStoreCount)} locations below privacy threshold</strong>
                  <span>{formatMoney(selectedMerchantRow.storeAttribution?.belowKStoreGmvCents)} is backed by fewer than k distinct consumers at the store level and is never itemized.</span>
                </div>
                <div className="ops-loop-detail-note">
                  <strong>{formatMoney(selectedMerchantRow.storeAttribution?.unresolvedGmvCents)} unresolved to a specific store</strong>
                  <span>Merchant spend with no usable transaction/store geography. Merchant geo completeness is {formatPercent(merchantTargeting.merchantGeographyCompletenessPercent)}.</span>
                </div>

                <h3>Observed Merchant Locations</h3>
                <div className="ops-loop-store-list">
                  {merchantObservedLocations(selectedMerchantRow).length ? merchantObservedLocations(selectedMerchantRow).map((loc) => (
                    <article key={loc.key || loc.label} className="ops-loop-store-card compact">
	                      <div>
	                        <strong>{loc.label}</strong>
	                        <span>{formatLocationAssignmentStatus(loc.marketAssignmentStatus)}</span>
	                      </div>
	                      <em>{formatMarketAttribution({ inTargetPolygon: loc.inTargetMarket, marketLabel: loc.marketLabel }, hasSpecificMarket)}</em>
                    </article>
                  )) : (
                    <div className="ops-loop-detail-empty">No merchant catalog locations are present on this rollup.</div>
                  )}
                </div>
                <div className="ops-loop-detail-note">
                  <strong>{formatStateLabel(selectedMerchantRow.pipelineStatus)}</strong>
                  <span>BD pipeline state from merchant-level operational records. Missing records default to not contacted.</span>
                </div>
              </div>
              <div className="ops-loop-detail-footer">
                <span>
                  Exports remain aggregate-gated: high confidence, confirmed, non-excluded, and at or above k-anonymity.
                </span>
                {selectedMerchantRow.platformBrandId && permissions.canViewStoreLevelDetail ? (
                  <button
                    type="button"
                    className="ops-btn primary"
                    onClick={() => navigate(`/ops/businesses/${encodeURIComponent(selectedMerchantRow.platformBrandId)}`)}
                  >
                    Open business
                  </button>
                ) : (
                  <button type="button" className="ops-btn" disabled>
                    No linked business account
                  </button>
                )}
              </div>
            </aside>
          </div>
        ) : null}

        {merchantModalOpen ? (
          <div
            className="ops-loop-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setMerchantModalOpen(false);
            }}
          >
            <section className="ops-loop-modal" role="dialog" aria-modal="true" aria-labelledby="loop-merchant-modal-title">
              <div className="ops-loop-modal-header">
                <div>
                  <span className="ops-loop-eyebrow">Merchant queue</span>
                  <h2 id="loop-merchant-modal-title">Close the loop next</h2>
                  <p>{formatCount(visibleMerchantRows.length)} privacy-safe rows in the current {formatStateLabel(merchantView)} view.</p>
                </div>
                <button type="button" className="ops-btn" onClick={() => setMerchantModalOpen(false)}>
                  Close
                </button>
              </div>
              <div className="ops-loop-modal-body">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Category</th>
                      <th>Ownership</th>
                      <th>Status</th>
                      <th>Frequency</th>
                      <th>Txn</th>
                      <th>GMV</th>
                      <th>Consumers</th>
                      <th>Location</th>
                      <th>Market</th>
                      <th>Pipeline</th>
                      <th>Export</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMerchantRows.length ? visibleMerchantRows.map((row) => (
                      <tr key={`modal_${merchantView}_${row.merchantRefId}`}>
                        <td>
	                          <MerchantNameButton row={row} onOpen={setSelectedMerchantRow} />
                        </td>
                        <td>{row.categoryLabel}</td>
                        <td>{formatOwnershipTier(row.ownershipTier)}</td>
                        <td>{formatStateLabel(row.classificationStatus)}</td>
                        <td>
                          <span className={`ops-loop-frequency-pill ${row.frequencyType || 'periodic'}`}>
                            <span className="ops-loop-frequency-icon" aria-hidden="true">{frequencyIcon(row.frequencyType)}</span>
                            {formatFrequencyType(row.frequencyType)}
                          </span>
                        </td>
                        <td>{formatCount(row.transactionCount)}</td>
                        <td>{formatMoney(row.gmvCents)}</td>
                        <td>{formatCount(row.distinctConsumers)}</td>
                        <td>
                          <MerchantLocationCell row={row} />
                        </td>
                        <td>
                          <MerchantMarketCell row={row} hasSpecificMarket={hasSpecificMarket} />
                        </td>
                        <td>{formatStateLabel(row.pipelineStatus)}</td>
                        <td>{row.exportEligible ? 'Eligible' : 'No'}</td>
                        <td>
                          <button type="button" className="ops-btn" onClick={() => setSelectedMerchantRow(row)}>
                            Details
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="13">No merchant rows in this view for the current slice.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </OpsLayout>
  );
}
